import { useState, useEffect, useCallback, useRef } from 'react';
import KohdeTabBar from './KohdeTabBar';
import TaulukkoFooter from './TaulukkoFooter';
import { useReportTemplateContext } from '../ReportTemplate/ReportTemplateContext';
import { useRakennusData } from '../../../context/RakennusDataContext';
import { defaultProjectName } from '../../../utils/projectNaming';
import { mittausCacheSet, mittausCacheSubscribe, subscribeNayteTunnus } from '../../../utils/mittausCache';
import { findLinkedProjectId } from '../../../utils/ptsUtils';
import {
  getDynamicPTSProjects,
  getMittausProjektitByPTS,
  createMittausProjekti,
  getMittausProjekti,
  renameMittausProjekti,
  deleteMittausProjekti,
  updateMittausProjektiMeta,
  createMittausKohde,
  updateMittausKohde,
  deleteMittausKohde,
} from '../../../api/api';

const newRow = () => ({
  id: crypto.randomUUID(),
  tunnus: '',
  laatuluokka: '',
  kuntoluokka: '',
  pakkasrapautuneisuus: '',
});

const LUOKKA_OPTIONS = ['Erinomainen', 'Hyvä', 'Tyydyttävä', 'Välttävä', 'Huono'];
const PAKKASRAPAUTUNEISUUS_OPTIONS = [
  'Ei rapautumaa',
  'Vähäistä',
  'Kohtalaista',
  'Voimakasta',
  'Ei koossapysyvää',
];

const C  = { border: '1px solid #adb5bd', padding: '6px 8px', fontSize: '0.9rem', verticalAlign: 'middle' };
const CH = { ...C, background: '#04aa00', color: '#fff', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap' };
const inp = { border: '1px solid #ced4da', borderRadius: 3, padding: '3px 6px', fontSize: '0.9rem', width: '100%', background: '#fff' };
const sel = { ...inp, cursor: 'pointer', textAlign: 'center' };

export default function OhuthieBlock({ data, onChange, card, tableStartNumber, subsectionTitle, onCreateNayte, ptsProjectName, allViewValue, onSetAllView }) {
  let ctxKiinteisto = null;
  let ctxSections = null;
  try {
    const ctx = useReportTemplateContext();
    ctxKiinteisto = ctx?.report?.id_kiinteisto ?? ctx?.rakennusData?.id_kiinteisto ?? null;
    ctxSections = ctx?.sections ?? null;
  } catch {}
  const { rakennusData } = useRakennusData?.() ?? {};
  const id_kiinteisto = ctxKiinteisto ?? rakennusData?.id_kiinteisto ?? null;
  const rakennukset = card?.rakennukset_fulls ?? [];
  const linkedPtsId = findLinkedProjectId(ctxSections);

  const [projekt, setProjekt]               = useState(null);
  const [allProjektit, setAllProjektit]     = useState([]);
  const [activeKohdeId, setActiveKohdeId]   = useState(null);
  const [loading, setLoading]               = useState(false);

  const kohteet     = projekt?.kohteet ?? [];
  const activeKohde = kohteet.find(k => k.id === activeKohdeId) ?? null;
  const activeIdx   = kohteet.findIndex(k => k.id === activeKohdeId);

  // Project selector overlay
  const [showSelector, setShowSelector]             = useState(false);
  const [ptsProjects, setPtsProjects]               = useState([]);
  const [selectedPtsId, setSelectedPtsId]           = useState(null);
  const [existingProjektit, setExistingProjektit]   = useState([]);
  const [newProjName, setNewProjName]               = useState('');
  const [newProjDate, setNewProjDate]               = useState('');
  const [newProjScope, setNewProjScope]             = useState('property');
  const [newProjRakennusId, setNewProjRakennusId]   = useState(null);
  const [projNameError, setProjNameError]           = useState('');
  const [apiError, setApiError]                     = useState('');
  const [selectorTab, setSelectorTab]               = useState('new'); // 'new' | 'existing'

  // Kohde management

  const allKohteet = allViewValue ?? true;
  const [nayteCreateError, setNayteCreateError]     = useState(null);
  const [isMobile, setIsMobile]                     = useState(() => window.innerWidth < 640);

  // Rename/delete project
  const [renamingProject, setRenamingProject]       = useState(false);
  const [renameProjectValue, setRenameProjectValue] = useState('');
  const [pendingDeleteProject, setPendingDeleteProject] = useState(false);

  // Debounced save per kohde
  const saveTimers = useRef({});
  const captionSaveTimers = useRef({});
  const scheduleSave = useCallback((kohdeId, data) => {
    clearTimeout(saveTimers.current[kohdeId]);
    saveTimers.current[kohdeId] = setTimeout(() => {
      updateMittausKohde(kohdeId, { data }).catch(console.error);
    }, 1200);
  }, []);

  useEffect(() => {
    if (!data?.mittausProjektiId) return;
    setLoading(true);
    getMittausProjekti(data.mittausProjektiId)
      .then(res => {
        setProjekt(res.data);
        mittausCacheSet(res.data.id, res.data);
        const kohteet = res.data.kohteet ?? [];
        setActiveKohdeId(kohteet[0]?.id ?? null);
      })
      .catch(() => setProjekt(null))
      .finally(() => setLoading(false));
  }, [data?.mittausProjektiId]);

  useEffect(() => {
    const ptsId = data?.ptsProjectId;
    if (!ptsId) return;
    getMittausProjektitByPTS(ptsId, 'ohuthie').then(res => {
      const list = res.data ?? [];
      setAllProjektit(list);
      if (list.length > 0 && !data?.mittausProjektiId) {
        setLoading(true);
        getMittausProjekti(list[0].id).then(r => {
          setProjekt(r.data);
          mittausCacheSet(r.data.id, r.data);
          setActiveKohdeId((r.data.kohteet ?? [])[0]?.id ?? null);
          onChange?.({ ...data, mittausProjektiId: r.data.id });
        }).catch(() => {}).finally(() => setLoading(false));
      }
    }).catch(() => {});
  }, [data?.ptsProjectId]);

  const switchToProject = async (id) => {
    setLoading(true);
    getMittausProjekti(id).then(res => {
      setProjekt(res.data);
      mittausCacheSet(res.data.id, res.data);
      setActiveKohdeId((res.data.kohteet ?? [])[0]?.id ?? null);
      onChange?.({ ...data, mittausProjektiId: id });
    }).catch(() => {}).finally(() => setLoading(false));
  };

  const suggestedName = (scope, rakennusId, date) => {
    if (scope === 'building') {
      const r = rakennukset.find(r => r.id_rakennus === rakennusId) ?? rakennukset[0];
      const tunnus = r?.rakennustunnus ?? '';
      return tunnus ? `Rakennus ${tunnus} ${date}` : `Rakennus ${date}`;
    }
    const addr = String(rakennukset[0]?.osoite ?? card?.osoite ?? '').split('\n')[0].trim();
    return addr ? `${addr} ${date}` : defaultProjectName('Ohuthie', subsectionTitle);
  };

  const refreshAllProjektit = (ptsId) =>
    getMittausProjektitByPTS(ptsId, 'ohuthie')
      .then(res => setAllProjektit(res.data ?? []))
      .catch(() => {});

  const openSelector = async () => {
    if (!id_kiinteisto) return;
    const today = new Date().toISOString().split('T')[0];
    const rid = rakennukset[0]?.id_rakennus ?? null;
    setNewProjDate(today);
    setNewProjScope('property');
    setNewProjRakennusId(rid);
    setNewProjName(defaultProjectName('Ohuthie', subsectionTitle));
    setProjNameError('');
    setApiError('');
    setSelectorTab('new');
    try {
      const res = await getDynamicPTSProjects(id_kiinteisto);
      const pts = res.data ?? [];
      setPtsProjects(pts);
      setSelectedPtsId(pts[0]?.id ?? null);
      setExistingProjektit([]);
      if (pts[0]?.id) {
        const r2 = await getMittausProjektitByPTS(pts[0].id, 'ohuthie');
        setExistingProjektit(r2.data ?? []);
      }
      setShowSelector(true);
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Lataus epäonnistui');
    }
  };

  const onPtsChange = async (ptsId) => {
    setSelectedPtsId(ptsId);
    try {
      const res = await getMittausProjektitByPTS(ptsId, 'ohuthie');
      setExistingProjektit(res.data ?? []);
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Lataus epäonnistui');
    }
  };

  const createProject = async () => {
    if (!selectedPtsId) return;
    const name = newProjName.trim();
    if (!name) { setProjNameError('Nimi vaaditaan.'); return; }
    setProjNameError('');
    setApiError('');
    try {
      const res = await createMittausProjekti(selectedPtsId, 'ohuthie', name, {
        paivamaara: newProjDate || null,
        scope: newProjScope,
        id_rakennus: newProjScope === 'building' ? (newProjRakennusId ?? null) : null,
      });
      const proj = res.data;
      setProjekt(proj);
      mittausCacheSet(proj.id, proj);
      setActiveKohdeId((proj.kohteet ?? [])[0]?.id ?? null);
      onChange?.({ ...data, mittausProjektiId: proj.id, ptsProjectId: selectedPtsId });
      refreshAllProjektit(selectedPtsId);
      setShowSelector(false);
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Tarkastuskohteen luonti epäonnistui');
    }
  };

  const linkProject = async (id) => {
    setApiError('');
    try {
      const res = await getMittausProjekti(id);
      const proj = res.data;
      setProjekt(proj);
      mittausCacheSet(proj.id, proj);
      setActiveKohdeId((proj.kohteet ?? [])[0]?.id ?? null);
      onChange?.({ ...data, mittausProjektiId: id, ptsProjectId: selectedPtsId });
      refreshAllProjektit(selectedPtsId);
      setShowSelector(false);
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Tarkastuskohteen lataus epäonnistui');
    }
  };

  const commitRenameProject = async () => {
    const trimmed = renameProjectValue.trim();
    if (!trimmed || !projekt) { setRenamingProject(false); return; }
    await renameMittausProjekti(projekt.id, trimmed);
    setProjekt(p => ({ ...p, nimi: trimmed }));
    setRenamingProject(false);
  };

  const confirmDeleteProject = async () => {
    if (!projekt) return;
    await deleteMittausProjekti(projekt.id);
    setProjekt(null);
    setActiveKohdeId(null);
    onChange?.({ ...data, mittausProjektiId: null });
    setPendingDeleteProject(false);
  };

  // Re-sync local state when syncPoranayteRow adds a kohde via cache
  useEffect(() => {
    if (!data?.mittausProjektiId) return;
    return mittausCacheSubscribe(data.mittausProjektiId, (updatedProj) => {
      setProjekt(updatedProj);
      setActiveKohdeId(prev => prev ?? updatedProj.kohteet?.[0]?.id ?? null);
    });
  }, [data?.mittausProjektiId]);

  useEffect(() => {
    if (!id_kiinteisto) return;
    getDynamicPTSProjects(id_kiinteisto).then(res => setPtsProjects(res.data ?? [])).catch(() => {});
  }, [id_kiinteisto]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!linkedPtsId || linkedPtsId === data?.ptsProjectId) return;
    onChange?.({ ...data, ptsProjectId: linkedPtsId, mittausProjektiId: null });
  }, [linkedPtsId, data?.ptsProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    return subscribeNayteTunnus((nayteId, newTunnus) => {
      setProjekt(prev => {
        if (!prev) return prev;
        let changed = false;
        const kohteet = prev.kohteet.map(k => {
          if (k.nayte_id !== nayteId) return k;
          changed = true;
          const d = [{ ...(k.data?.[0] ?? {}), tunnus: newTunnus }];
          updateMittausKohde(k.id, { data: d }).catch(console.error);
          return { ...k, data: d };
        });
        return changed ? { ...prev, kohteet } : prev;
      });
    });
  }, []);

  const handleKohdeCreate = async (name, withNayte = false, tunnus = '') => {
    let nayteId = null;
    if (withNayte && onCreateNayte) {
      try { const n = await onCreateNayte(name, tunnus, 'Ohuthie'); nayteId = n?.id ?? null; }
      catch (err) { if (err?.code === 'DUPLICATE_TUNNUS') { setNayteCreateError(err.message); return null; } }
    }
    const res = await createMittausKohde(projekt.id, name, projekt.kohteet?.length ?? 0, nayteId);
    const kohde = { ...res.data, nayte_id: nayteId, data: [{ ...newRow(), ...(tunnus ? { tunnus } : {}) }] };
    await updateMittausKohde(kohde.id, { data: kohde.data });
    const updatedProjekt = { ...projekt, kohteet: [...(projekt.kohteet ?? []), kohde] };
    setProjekt(updatedProjekt);
    mittausCacheSet(updatedProjekt.id, updatedProjekt);
    setActiveKohdeId(kohde.id);
    return kohde;
  };

  const handleKohdeRename = async (id, name) => {
    await updateMittausKohde(id, { nimi: name });
    setProjekt(p => ({ ...p, kohteet: p.kohteet.map(k => k.id === id ? { ...k, nimi: name } : k) }));
  };

  const handleKohdeDelete = async (id) => {
    await deleteMittausKohde(id);
    const kohteet = (projekt?.kohteet ?? []).filter(k => k.id !== id);
    setProjekt(p => ({ ...p, kohteet }));
    if (activeKohdeId === id) setActiveKohdeId(kohteet[0]?.id ?? null);
  };

  // Auto-add one initial row when a newly-created kohde has no data
  useEffect(() => {
    if (loading || !activeKohde || (activeKohde.data ?? []).length > 0) return;
    setProjekt(prev => ({
      ...prev,
      kohteet: (prev?.kohteet ?? []).map(k => {
        if (k.id !== activeKohdeId || (k.data ?? []).length > 0) return k;
        const d = [newRow()];
        updateMittausKohde(k.id, { data: d }).catch(() => {});
        return { ...k, data: d };
      }),
    }));
  }, [loading, activeKohdeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateInKohde = (kohdeId, field, value) => {
    setProjekt(prev => {
      const updated = prev.kohteet.map(k => {
        if (k.id !== kohdeId) return k;
        const existing = (k.data ?? [])[0] ?? newRow();
        const d = [{ ...existing, [field]: value }];
        scheduleSave(k.id, d);
        return { ...k, data: d };
      });
      return { ...prev, kohteet: updated };
    });
  };


  if (!id_kiinteisto) return (
    <div>
      <div className="text-muted small p-2">Kiinteistön ID ei saatavilla.</div>
      <TaulukkoFooter taulukkoNum={tableStartNumber ?? 1} kohdeId="preview" caption={null} onChange={() => {}} />
    </div>
  );

  if (loading) {
    return <div className="text-muted small p-2">Ladataan…</div>;
  }

  return (
    <div style={{ fontSize: '0.95rem' }}>

      {(ptsProjectName || data?.ptsProjectId) && (
        <div style={{ fontSize: '0.72rem', color: '#6c757d', marginBottom: 6 }}>
          {ptsProjectName ?? ptsProjects.find(p => p.id === data?.ptsProjectId)?.name ?? ptsProjects.find(p => p.id === data?.ptsProjectId)?.nimi ?? `PTS ${data.ptsProjectId}`}
        </div>
      )}
      {/* ── Project header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.68rem', color: '#6c757d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Tarkastuskohde:</span>
        {allProjektit.length > 0 ? (
          <>
            {allProjektit.map(p => (
              <button key={p.id} type="button" onClick={() => switchToProject(p.id)}
                style={{ padding: '2px 8px', fontSize: '0.78rem', border: '1px solid', borderRadius: 3, cursor: 'pointer',
                  borderColor: p.id === projekt?.id ? '#198754' : '#dee2e6',
                  background: p.id === projekt?.id ? '#f0fdf4' : '#fff',
                  color: p.id === projekt?.id ? '#15803d' : '#6c757d',
                  fontWeight: p.id === projekt?.id ? 600 : 400 }}>
                {p.nimi}
              </button>
            ))}
            {projekt && (
              <button type="button" onClick={() => setPendingDeleteProject(true)}
                style={{ background: 'none', border: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1rem', padding: 0, lineHeight: 1 }} onMouseEnter={e => e.currentTarget.style.color='#dc3545'} onMouseLeave={e => e.currentTarget.style.color='#adb5bd'} title="Poista aktiivinen tarkastuskohde">×</button>
            )}
          </>
        ) : projekt ? (
          <>
            {renamingProject ? (
              <input autoFocus value={renameProjectValue} onChange={e => setRenameProjectValue(e.target.value)}
                onBlur={commitRenameProject}
                onKeyDown={e => { if (e.key === 'Enter') commitRenameProject(); if (e.key === 'Escape') setRenamingProject(false); }}
                style={{ fontSize: '0.82rem', fontWeight: 700, border: 'none', borderBottom: '1px solid #198754', outline: 'none', background: 'transparent', padding: '0 2px' }} />
            ) : (
              <span style={{ fontWeight: 600, fontSize: '0.82rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                onDoubleClick={() => { setRenameProjectValue(projekt.nimi); setRenamingProject(true); }}>
                {projekt.nimi}
              </span>
            )}
            <button type="button" onClick={() => setPendingDeleteProject(true)}
              style={{ background: 'none', border: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1rem', padding: 0, lineHeight: 1 }} onMouseEnter={e => e.currentTarget.style.color='#dc3545'} onMouseLeave={e => e.currentTarget.style.color='#adb5bd'} title="Poista tarkastuskohde">×</button>
          </>
        ) : (
          <button type="button" onClick={openSelector}
            style={{ padding: '3px 10px', borderRadius: 20, border: '1px dashed #0d6efd', background: '#fff', color: '#0d6efd', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600 }}>
            + Valitse / luo tarkastuskohde
          </button>
        )}
      </div>

      {projekt && (
        <>
          {/* ── Kohde tabs + toggle ── */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <KohdeTabBar
                kohteet={kohteet}
                activeKohdeId={activeKohdeId}
                tableStartNumber={tableStartNumber != null ? tableStartNumber : undefined}
                onActivate={id => { setActiveKohdeId(id); onSetAllView?.(false); }}
                onRename={handleKohdeRename}
                onDelete={handleKohdeDelete}
                hideTabs={allKohteet}
                displayNimi={k => [k.nimi, k.data?.[0]?.tunnus].filter(Boolean).join(' — ')}
              />
            </div>
            <button
              type="button"
              onClick={() => onSetAllView?.(!allKohteet)}
              title={allKohteet ? 'Näytä välilehdet' : 'Näytä kaikki rinnakkain'}
              style={{ flexShrink: 0, padding: '4px 8px', border: '1px solid #dee2e6', borderRadius: 4, background: allKohteet ? '#198754' : '#fff', color: allKohteet ? '#fff' : '#6c757d', fontSize: '1rem', cursor: 'pointer', marginTop: 2 }}
            >⇄</button>
          </div>

          {nayteCreateError && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => setNayteCreateError(null)}>
              <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(360px, 92vw)', padding: '28px 28px 22px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}
                onClick={e => e.stopPropagation()}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#fff3cd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem' }}>⚠️</div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a' }}>Näytetunnus on jo olemassa</div>
                <div style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.5 }}>Voit lisätä testejä näytteelle poranäyte taulukosta.</div>
                <button type="button" className="btn btn-warning btn-sm mt-1" onClick={() => setNayteCreateError(null)} style={{ minWidth: 80 }}>OK</button>
              </div>
            </div>
          )}

          {/* ── Table / Cards ── */}
          {(() => {
            const visibleKohteet = allKohteet ? kohteet : (activeKohde ? [activeKohde] : []);
            if (!visibleKohteet.length) return null;
            const LBL = { fontSize: '0.68rem', color: '#6c757d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 2 };
            if (isMobile) {
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {visibleKohteet.map(k => {
                    const row = (k.data ?? [])[0] ?? {};
                    return (
                      <div key={k.id} style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '10px 12px', background: '#fafafa' }}>
                        {allKohteet && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #e9ecef' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.85rem', flex: 1 }}>{k.nimi}</span>
                            <button type="button" onClick={() => handleKohdeDelete(k.id)}
                              style={{ background: 'none', border: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1.1rem', padding: 0, lineHeight: 1 }}
                              onMouseEnter={e => e.currentTarget.style.color='#dc3545'} onMouseLeave={e => e.currentTarget.style.color='#adb5bd'}
                              title="Poista rakenneosa">×</button>
                          </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <div>
                            <div style={LBL}>Tunnus</div>
                            <div style={{ fontSize: '0.9rem', fontWeight: 500, padding: '3px 0', color: row.tunnus ? '#212529' : '#adb5bd', fontStyle: row.tunnus ? 'normal' : 'italic' }}>{row.tunnus || '—'}</div>
                          </div>
                          <div>
                            <div style={LBL}>Laatuluokka</div>
                            <select style={sel} value={row.laatuluokka ?? ''} onChange={e => updateInKohde(k.id, 'laatuluokka', e.target.value)}>
                              <option value="">–</option>
                              {LUOKKA_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                          <div>
                            <div style={LBL}>Kuntoluokka</div>
                            <select style={sel} value={row.kuntoluokka ?? ''} onChange={e => updateInKohde(k.id, 'kuntoluokka', e.target.value)}>
                              <option value="">–</option>
                              {LUOKKA_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={LBL}>Pakkasrapautuneisuus</div>
                            <select style={{ ...inp, textAlign: 'center' }} value={row.pakkasrapautuneisuus ?? ''} onChange={e => updateInKohde(k.id, 'pakkasrapautuneisuus', e.target.value)}>
                              <option value="">–</option>
                              {PAKKASRAPAUTUNEISUUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }
            return (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      {allKohteet && <th style={CH}>Rakenneosa</th>}
                      <th style={CH}>Tunnus</th>
                      <th style={CH}>Laatu-<br />luokka</th>
                      <th style={CH}>Kunto-<br />luokka</th>
                      <th style={CH}>Pakkasrapau-<br />tuneisuus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleKohteet.map(k => {
                      const row = (k.data ?? [])[0] ?? {};
                      return (
                        <tr key={k.id}>
                          {allKohteet && (
                            <td style={{ ...C, fontWeight: 600, fontSize: '0.8rem', color: '#495057', textAlign: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                <span>{k.nimi}</span>
                                <button type="button" onClick={() => handleKohdeDelete(k.id)}
                                  style={{ background: 'none', border: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1rem', padding: 0, lineHeight: 1, flexShrink: 0 }} onMouseEnter={e => e.currentTarget.style.color='#dc3545'} onMouseLeave={e => e.currentTarget.style.color='#adb5bd'}
                                  title="Poista rakenneosa">×</button>
                              </div>
                            </td>
                          )}
                          <td style={{ ...C, textAlign: 'center', fontWeight: 500 }}>{row.tunnus || <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>—</span>}</td>
                          <td style={C}>
                            <select style={sel} value={row.laatuluokka ?? ''} onChange={e => updateInKohde(k.id, 'laatuluokka', e.target.value)}>
                              <option value="">–</option>
                              {LUOKKA_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </td>
                          <td style={C}>
                            <select style={sel} value={row.kuntoluokka ?? ''} onChange={e => updateInKohde(k.id, 'kuntoluokka', e.target.value)}>
                              <option value="">–</option>
                              {LUOKKA_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </td>
                          <td style={C}>
                            <select style={{ ...inp, textAlign: 'center' }} value={row.pakkasrapautuneisuus ?? ''} onChange={e => updateInKohde(k.id, 'pakkasrapautuneisuus', e.target.value)}>
                              <option value="">–</option>
                              {PAKKASRAPAUTUNEISUUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </>
      )}

      <TaulukkoFooter
        taulukkoNum={tableStartNumber != null ? tableStartNumber + Math.max(0, activeIdx) : Math.max(0, activeIdx) + 1}
        kohdeId={activeKohdeId ?? 'preview'}
        caption={data?.caption}
        onChange={capData => onChange?.({ ...data, caption: capData })}
      />

      {/* ── Project selector overlay ── */}
      {showSelector && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowSelector(false); }}>
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(480px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Ohuthie-tarkastuskohde</div>

            {/* PTS project picker */}
            <div>
              <label style={{ fontSize: '0.82rem', color: '#6c757d', fontWeight: 600, marginBottom: 4, display: 'block' }}>Dynamic PTS -projekti</label>
              <select className="form-select form-select-sm" value={selectedPtsId ?? ''} onChange={e => onPtsChange(Number(e.target.value))}>
                {ptsProjects.map(p => <option key={p.id} value={p.id}>{p.name ?? p.nimi}</option>)}
              </select>
            </div>

            {/* Tabs: new / existing */}
            <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6', gap: 0 }}>
              {['new', 'existing'].map(tab => (
                <button key={tab} type="button" onClick={() => setSelectorTab(tab)}
                  style={{ padding: '6px 16px', border: 'none', borderBottom: tab === selectorTab ? '2px solid #0d6efd' : '2px solid transparent', background: 'none', color: tab === selectorTab ? '#0d6efd' : '#6c757d', fontWeight: tab === selectorTab ? 600 : 400, fontSize: '0.88rem', cursor: 'pointer' }}>
                  {tab === 'new' ? 'Uusi tarkastuskohde' : `Olemassa olevat (${existingProjektit.length})`}
                </button>
              ))}
            </div>

            {selectorTab === 'new' ? (
              <>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057', display: 'block', marginBottom: 3 }}>Tarkastuskohteen nimi</label>
                  <input className="form-control form-control-sm" autoFocus value={newProjName}
                    onChange={e => { setNewProjName(e.target.value); setProjNameError(''); }}
                    onKeyDown={e => e.key === 'Enter' && newProjName.trim() && createProject()}
                    style={{ borderColor: projNameError ? '#dc3545' : undefined }} />
                  {projNameError && <div style={{ fontSize: '0.7rem', color: '#dc3545', marginTop: 3 }}>{projNameError}</div>}
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057', display: 'block', marginBottom: 3 }}>Päivämäärä</label>
                  <input className="form-control form-control-sm" type="date" value={newProjDate} onChange={e => setNewProjDate(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057', display: 'block', marginBottom: 6 }}>Laajuus</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[['property', 'Koko kiinteistö'], ['building', 'Yksittäinen rakennus']].map(([scope, label]) => (
                      <button key={scope} type="button"
                        onClick={() => { setNewProjScope(scope); setNewProjName(suggestedName(scope, newProjRakennusId, newProjDate)); setProjNameError(''); }}
                        style={{ flex: 1, padding: '7px 12px', border: '1px solid', borderRadius: 4, borderColor: newProjScope === scope ? '#198754' : '#ced4da', background: newProjScope === scope ? '#198754' : '#fff', color: newProjScope === scope ? '#fff' : '#495057', fontSize: '0.82rem', cursor: 'pointer', fontWeight: newProjScope === scope ? 600 : 400 }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {newProjScope === 'building' && rakennukset.length > 0 && (
                  <div>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057', display: 'block', marginBottom: 3 }}>Rakennus</label>
                    <select className="form-select form-select-sm" value={newProjRakennusId ?? ''}
                      onChange={e => { const rid = parseInt(e.target.value); setNewProjRakennusId(rid); setNewProjName(suggestedName('building', rid, newProjDate)); setProjNameError(''); }}>
                      {rakennukset.map(r => <option key={r.id_rakennus} value={r.id_rakennus}>{[r.rakennustunnus, r.osoite, r.toimipaikka].filter(Boolean).join(' — ') || `Rakennus ${r.id_rakennus}`}</option>)}
                    </select>
                  </div>
                )}
                {apiError && (
                  <div style={{ fontSize: '0.78rem', color: '#842029', background: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 4, padding: '6px 10px' }}>
                    {apiError}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowSelector(false)}>Peruuta</button>
                  <button className="btn btn-sm btn-success" onClick={createProject} disabled={!selectedPtsId || !newProjName.trim()}>Luo tarkastuskohde</button>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                {existingProjektit.length === 0 ? (
                  <div style={{ fontSize: '0.88rem', color: '#6c757d' }}>Ei ohuthie-tarkastuskohteita tässä PTS-projektissa.</div>
                ) : existingProjektit.map(p => (
                  <button key={p.id} type="button" onClick={() => linkProject(p.id)}
                    style={{ textAlign: 'left', padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.9rem' }}>
                    {p.nimi}
                  </button>
                ))}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowSelector(false)}>Sulje</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Delete project confirmation ── */}
      {pendingDeleteProject && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setPendingDeleteProject(false); }}>
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(360px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Poista tarkastuskohde</div>
            <div style={{ fontSize: '0.92rem', color: '#495057' }}>
              Haluatko poistaa tarkastuskohteen <strong>{projekt?.nimi}</strong> kaikkine kohteineen?
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setPendingDeleteProject(false)}>Peru</button>
              <button className="btn btn-sm btn-danger" onClick={confirmDeleteProject}>Poista</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
